import { Loader2, Palette, Plus, Search } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useConfirm, useToast } from "@/components/shared";
import { getApiErrorMessage } from "@/lib/api";
import { brandService } from "@/lib/services";
import type { Brand, BrandSummary } from "@/types";
import { BrandAccessModal } from "./brands/BrandAccessModal";
import { BrandCard } from "./brands/BrandCard";
import { BrandFormModal } from "./brands/BrandFormModal";

function EmptyState({ onCreate }: Readonly<{ onCreate: () => void }>) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="h-14 w-14 rounded-2xl bg-brand-primary/10 flex items-center justify-center mb-4">
        <Palette className="h-7 w-7 text-brand-primary" />
      </div>
      <h3 className="text-lg font-semibold text-content-primary">No brands yet</h3>
      <p className="text-sm text-content-tertiary mt-1 max-w-md">
        Create a brand to tailor generated dashboards and documents — internal vs. external, or per product.
      </p>
      <button
        type="button"
        onClick={onCreate}
        className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-brand-gradient hover:opacity-90 text-white rounded-lg text-sm font-medium transition"
      >
        <Plus className="h-4 w-4" /> Create brand
      </button>
    </div>
  );
}

export function Brands() {
  const { confirm } = useConfirm();
  const { showToast } = useToast();
  const [brands, setBrands] = useState<BrandSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Brand | null>(null);
  const [sharing, setSharing] = useState<BrandSummary | null>(null);
  const openEditTokenRef = useRef(0);

  const refresh = async () => {
    try {
      const res = await brandService.list();
      setBrands(res.data);
    } catch (error) {
      console.error("Failed to load brands:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const list = q
      ? brands.filter((b) => b.name.toLowerCase().includes(q) || (b.audience ?? "").toLowerCase().includes(q))
      : brands;
    return [...list].sort((a, b) => Number(b.isDefault) - Number(a.isDefault) || a.name.localeCompare(b.name));
  }, [brands, searchQuery]);

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = async (uuid: string) => {
    // Guard the async fetch: the LAST click wins even if an earlier fetch
    // resolves after a later one (otherwise the modal can show the wrong brand).
    openEditTokenRef.current += 1;
    const token = openEditTokenRef.current;
    try {
      const res = await brandService.get(uuid);
      if (token !== openEditTokenRef.current) return; // superseded by a newer click
      setEditing(res.data);
      setModalOpen(true);
    } catch (err) {
      if (token !== openEditTokenRef.current) return;
      showToast(getApiErrorMessage(err, "Failed to open brand"), "error");
    }
  };

  const openShare = (uuid: string) => {
    const brand = brands.find((b) => b.uuid === uuid);
    if (brand) setSharing(brand);
  };

  const handleDelete = async () => {
    if (!editing) return;
    const confirmed = await confirm({
      title: "Delete brand",
      message: `Delete “${editing.name}”? This cannot be undone.`,
      confirmText: "Delete",
      variant: "danger",
    });
    if (!confirmed) return;
    try {
      await brandService.delete(editing.uuid);
      showToast("Brand deleted", "success");
      setModalOpen(false);
      setEditing(null);
      await refresh();
    } catch (err) {
      showToast(getApiErrorMessage(err, "Failed to delete brand"), "error");
    }
  };

  let brandListContent: ReactNode;
  if (loading) {
    brandListContent = (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 text-brand-primary animate-spin" />
      </div>
    );
  } else if (filtered.length === 0) {
    // Distinguish "no brands exist" (offer the create CTA) from "search
    // matched nothing" (offer to clear the search).
    brandListContent =
      brands.length === 0 ? (
        <EmptyState onCreate={openCreate} />
      ) : (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <p className="text-sm text-content-tertiary">No brands match “{searchQuery.trim()}”.</p>
          <button
            type="button"
            onClick={() => setSearchQuery("")}
            className="mt-3 text-sm text-brand-primary hover:underline"
          >
            Clear search
          </button>
        </div>
      );
  } else {
    brandListContent = (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((brand) => (
          <BrandCard key={brand.uuid} brand={brand} onOpen={openEdit} onShare={openShare} />
        ))}
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="shrink-0 p-6 border-b border-border-primary">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-content-primary">Brands</h1>
            <p className="text-sm text-content-tertiary mt-0.5">
              Brand kits used to style generated dashboards and documents.
            </p>
          </div>
          <button
            type="button"
            onClick={openCreate}
            className="inline-flex items-center gap-2 px-4 py-2 bg-brand-gradient hover:opacity-90 text-white rounded-lg text-sm font-medium transition shrink-0"
          >
            <Plus className="h-4 w-4" /> Create brand
          </button>
        </div>
        <div className="relative mt-4 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-content-tertiary" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search brands"
            className="w-full pl-9 pr-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-tertiary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 text-sm"
          />
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6">{brandListContent}</div>

      {modalOpen && (
        <BrandFormModal
          brand={editing}
          onClose={() => {
            setModalOpen(false);
            setEditing(null);
          }}
          onSaved={refresh}
          onDelete={editing?.canManage && !editing.isDefault ? handleDelete : undefined}
        />
      )}

      {sharing && (
        <BrandAccessModal
          brandUuid={sharing.uuid}
          brandName={sharing.name}
          isOpen={sharing !== null}
          onClose={() => setSharing(null)}
        />
      )}
    </div>
  );
}
